import { v4 as uuid } from "uuid";
import { getBettingChannel } from "@/utilities/bettingChannels";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { isAuthed } from "@/features/auth/models/authState";
import {
  buildPayload,
  type SubmissionPayload,
} from "@/features/betslip/services/submitBet/buildPayload";
import {
  isSubmittableSelection,
  selectComboLegSetSubmittable,
  selectSubmittableCombinations,
} from "@/features/betslip/stores/computed";
import type { BetSelection, OddsPreference } from "@/features/betslip/types";
import type {
  BetslipStoreHandle,
  SubmitBetParams,
} from "@/features/betslip/services/submitBet/types";

const acceptOddsValue = (preference: OddsPreference): string =>
  preference === "AUTO"
    ? "ANY"
    : preference === "AUTO_HIGHER"
      ? "HIGHER"
      : "NONE";

/** The user's self-imposed event filters from the session. */
const getUserEventFilters = (): string[] => {
  const state = useAuthStore.getState();
  if (isAuthed(state)) {
    return state.session.attributes.eventFilters ?? [];
  }
  return [];
};

const fitsEventFilters = (
  selection: BetSelection,
  userEventFilters: string[],
): boolean => {
  if (userEventFilters.length === 0) return true;
  const selectionFilters = new Set(selection.filters);
  return userEventFilters.every((filter) => selectionFilters.has(filter));
};

type PreparedPayload = {
  payload: SubmissionPayload;
  /** outcomeId → correlationId per multi leg. Used to route per-leg rejections. */
  multiLegCorrelationIds: Record<string, string>;
  /** The multi bet's top-level correlationId, if a multi is in this submission. */
  multiCorrelationId: string | undefined;
  /**
   * Bet correlationId → slip selection key for single bets. Fallback route
   * for responses whose echoed outcomeIds can't be matched to a selection
   * (mirrors legacy's getOutcomeIdByCorrelation fallback).
   */
  betCorrelationToSelection: Record<string, string>;
};

export const preparePayload = (
  store: ReturnType<BetslipStoreHandle["getState"]>,
  params: SubmitBetParams,
  hasMultiStake: boolean,
): PreparedPayload => {
  // Pre-generate per-leg correlationIds so per-leg multi rejections
  // (`propertyPath: "selections.<correlationId>"`) can be routed back to
  // their originating outcomeId in `applyMultiRejection`.
  const multiLegCorrelationIds: Record<string, string> = {};
  for (const leg of store.multiLegs) {
    multiLegCorrelationIds[leg.outcomeId] = uuid();
  }

  // Last line of defence behind the footer gate: event filters, permissions
  // and market status each drop a selection; the rest of the slip still goes.
  const userEventFilters = getUserEventFilters();
  const submittableSelections: Record<string, BetSelection> = {};
  for (const [outcomeId, selection] of Object.entries(store.selections)) {
    if (
      fitsEventFilters(selection, userEventFilters) &&
      isSubmittableSelection(store, selection)
    ) {
      submittableSelections[outcomeId] = selection;
    }
  }

  // Pre-generate bet-level correlationIds for singles so responses can be
  // routed back by correlationId when outcomeIds matching fails.
  const singleCorrelationIds: Record<string, string> = {};
  const betCorrelationToSelection: Record<string, string> = {};
  for (const outcomeId of Object.keys(submittableSelections)) {
    const correlationId = uuid();
    singleCorrelationIds[outcomeId] = correlationId;
    betCorrelationToSelection[correlationId] = outcomeId;
  }

  // Multis and combos are all-or-nothing: one bad leg drops the product
  // rather than placing a different bet than the one on screen. Event filters
  // are re-checked here in case useSyncNotPermitted's effect lags a render.
  const comboLegSetSubmittable =
    selectComboLegSetSubmittable(store) &&
    store.multiLegs.every((leg) => fitsEventFilters(leg, userEventFilters));

  const multiHasLegRejection = store.multiLegs.some(
    (leg) => store.multiLegRejections[leg.outcomeId] !== undefined,
  );
  const multiLegSetSubmittable =
    comboLegSetSubmittable && !multiHasLegRejection;

  const submittableCombinations = comboLegSetSubmittable
    ? selectSubmittableCombinations(store)
    : {};

  const payload = buildPayload({
    selections: submittableSelections,
    singleStakes: store.stakes.singles,
    singlePromoCash: store.promoCash.singles,
    singleTokens: store.tokens.singles,
    multiStake:
      hasMultiStake && multiLegSetSubmittable ? store.stakes.multi : 0,
    multiPromoCash: store.promoCash.multi,
    multiToken: store.tokens.multi,
    multiLegs: store.multiLegs,
    combinations: submittableCombinations,
    alternativeStakeReferences: store.alternativeStakeReferences,
    multiAlternativeStakeReference: store.multiAlternativeStakeReference,
    multiLegCorrelationIds,
    singleCorrelationIds,
    currency: params.currency,
    acceptOddsChanges: acceptOddsValue(params.preference),
    channel: getBettingChannel(),
  });

  // The multi bet is the only one with >1 inner selections that isn't
  // a combo / exotic / quaddie. Identify it so the response handler can
  // recognise the multi reply by correlationId.
  const multiCorrelationId = payload.bets.find(
    (b) =>
      (b.selections?.length ?? 0) > 1 &&
      b.selections.every((s) => s.selectionType !== "Quaddie") &&
      b.exoticCombinations === undefined &&
      b.quaddieMarketId === undefined &&
      b.selectionsRequiredToWin === undefined,
  )?.correlationId;

  return {
    payload,
    multiLegCorrelationIds,
    multiCorrelationId,
    betCorrelationToSelection,
  };
};
